import {
	AfterViewChecked,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import { EngineService } from '@core/services/engine/engine.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { SaveSessionBlindsService } from '@core/services/save-session-blinds/save-session-blinds.service';
import { INTERIOR_FRAME_TOP_STYLE, SELECTORS, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { debounceTime, filter, Subject, Subscription } from 'rxjs';
import { ShareService } from '@core/services/share-data/share-data.service';
import { VariantService } from '@core/services/variant/variant.service';
import * as _ from 'lodash';
import { BlindStateService } from '@core/services/blind-state/blind-state.service';
import { BlindPartsAvailability, BlindSetup, SectionKeysEnum } from '@root/app.interfaces';
import { PartAvailabilityService } from '@core/services/part-availability/part-availability.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalService } from '@core/services/modal/modal.service';
import { DynamicCarouselModalComponent } from '@shared/modals/dynamic-carousel-modal/dynamic-carousel-modal.component';
import { MEASURE_GUIDE_MOCK } from '@features/size-change/measure-guide-mock';

declare const $: any;

@Component({
	selector: 'app-size-change',
	templateUrl: './size-change.component.html',
	styleUrls: ['./size-change.component.scss', './size-change.responsive.scss'],
})
export class SizeChangeComponent implements OnInit, OnDestroy, AfterViewChecked, OnChanges {
	@Input() blindId: number;
	@Output() sizeData = new EventEmitter();

	private sliderSizeUpdate = new Subject();

	getBlindSelect: Subscription;
	getMaterialType: Subscription;
	getMaterialColorChanged: Subscription;
	getOperationName: Subscription;
	getSceneCreated: Subscription;
	getViewType: Subscription;
	getBoundingBoxSize: Subscription;
	availabilitySubs: Subscription;

	storageSizes = {
		size: {},
	};

	storageMaterialType;
	serverSizeData;
	currentStorageMaterialType;

	modelSize;
	valueWidth;
	valueHeight;
	modelSizeVert;
	colorType: string;
	getBlindStatus: number | string;
	modelType: string;
	operationName: string;
	currentBlindId: number;

	sizeValue = {
		width: 0,
		height: 0,
	};

	dataValue = {
		width: '',
		height: '',
	};
	blindType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
	sessionConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config);
	defaultSizeData = this.sessionStorageService.getModelSize(STORAGE_NAMES.default_model_size);
	isDesignType: boolean = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.design;

	setServerData = false;
	hasExtraSizeWidth = false;
	hasExtraSizeHeight = false;
	firstOpenSizeError = false;

	protected isShop = this.variant.isShop();
	dontSet = this.variant.isShop();
	widthSet = !this.dontSet;
	heightSet = !this.dontSet;

	availabilityWarningMessages: string[];
	isSizeBlockedByAvailability = false;

	constructor(
		public engineService: EngineService,
		public sessionStorageService: SessionStorageService,
		public shareDataService: ShareService,
		public saveSessionBlind: SaveSessionBlindsService,
		public changeDetection: ChangeDetectorRef,
		private variant: VariantService,
		private blindStateService: BlindStateService,
		private partAvailabilityService: PartAvailabilityService,
		private modalService: ModalService,
		private destroyRef: DestroyRef,
	) {}

	ngOnInit(): void {
		if (this.sessionConfig) {
			this.getStorageMaterialType();
			this.getServerDefaultData();
			this.getStorageSizes();
		}

		const blind = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		this.dontSet = blind?.setup?.size ? !blind.setup.size.configured : this.dontSet;
		this.widthSet = !this.dontSet;
		this.heightSet = !this.dontSet;

		this.getBlindSelect = this.shareDataService.getBlindSelect.subscribe((res) => {
			this.getBlindStatus = res.id;
			this.modelType = this.modelType || res?.type;

			if (this.modelType && this.modelType === res?.type) {
				this.setBlindSelectId(res);
			}
		});
		this.getOperationName = this.shareDataService.getOperationName.subscribe((res) => (this.operationName = res));

		this.getMaterialColorChanged = this.shareDataService.getMaterialColorChanged
			.pipe(filter(({ blind_id }) => blind_id === this.blindId))
			.subscribe((res) => (this.colorType = res.type));
		this.getMaterialType = this.shareDataService.getMaterialType
			.pipe(filter(({ blindId }) => blindId === this.blindId))
			.subscribe(({ type }) => {
				this.colorType = type;
				if (this.modelType) {
					this.setMaterialTypeSize(this.colorType);
				}
			});

		this.getSceneCreated = this.shareDataService.getSceneCreated.subscribe((res) => {
			this.modelType = res;
			const getCurrentBLindId = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_current_blind_id);

			if (this.currentBlindId === getCurrentBLindId) {
				this.setMaterialTypeSize(this.colorType);
				this.setBlindSelectId({ id: this.getBlindStatus });
				this.setIVBlindSetup();
			}
		});

		this.getBoundingBoxSize = this.engineService.getBoundingBoxSize().subscribe((res) => {
			this.onBoundingBoxChange(res);
		});

		this.viewTypeHandler();
		this.setAvailabilitySubs();
		this.addSliderUpdateSubscription();
	}

	ngOnChanges(changes: SimpleChanges) {
		this.currentBlindId = changes.blindId.currentValue;
	}

	ngAfterViewChecked(): void {
		if (this.hasExtraSizeWidth || this.hasExtraSizeHeight) {
			this.openSizeItem();
		}
	}

	ngOnDestroy(): void {
		this.getBlindSelect.unsubscribe();
		this.getMaterialType.unsubscribe();
		this.getMaterialColorChanged.unsubscribe();
		this.getSceneCreated.unsubscribe();
		this.getBoundingBoxSize.unsubscribe();
		this.getViewType.unsubscribe();
		this.availabilitySubs.unsubscribe();
	}

	setCorrectSizesHandler(data): void {
		for (const item of data) {
			if (this.storageMaterialType === item.type) {
				this.serverSizeData = item.sizes;
			}
		}

		this.engineService.setGizmoMinMaxMeshScaling(this.serverSizeData);
	}

	getStorageSizes(): void {
		const sizeStorage = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data);

		if (sizeStorage) {
			for (const item of sizeStorage) {
				if (item.setup && item.setup.size) {
					this.storageSizes.size = item.setup.size;
					if (this.blindType === 'interior' && item.type === 'interior') {
						this.getInteriorTopStyle(false);
					}

					this.dataValue['height'] = this.storageSizes.size['height'];
					this.dataValue['width'] = this.storageSizes.size['width'];

					if (this.serverSizeData) {
						this.valueHeight = +this.storageSizes.size['height'];
						this.valueWidth = +this.storageSizes.size['width'];
					}
				}
			}
		}
	}

	getStorageMaterialType(): void {
		const sizeStorage = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data);

		if (sizeStorage) {
			for (const item of sizeStorage) {
				if (item.setup && item.setup.material) {
					this.storageMaterialType = item.setup.material.type;
				}
				if (item.setup && item.setup.operation) {
					this.operationName = item.setup.operation.name;
				}
			}
		}
	}

	updateSetting(type, event): void {
		if (type === 'width') {
			this.modelSize = event;
		}

		if (type === 'height') {
			this.modelSizeVert = event;
		}

		this.saveBlindSetup(this.storageSizes, true, true);
		this.saveSessionBlind.PutStorageDataToServer();
	}

	onBoundingBoxChange({ blindId, type, value, dragStatus }): void {
		if (+blindId === this.blindId) {
			this.onInputChangeHandler(
				type,
				value,
				{
					isBoundingBox: true,
					dragStatus,
				},
				true,
			);

			if (!dragStatus) {
				this.saveBlindSetup(this.storageSizes, true, true);
			}
		}
	}

	onInputChange(type: 'width' | 'height', size: number): void {
		const modelSize = this.sizeToPointConverter(size);
		const max = this.sizeToPointConverter(this.serverSizeData[type]?.maximum);
		const min = this.sizeToPointConverter(this.serverSizeData[type]?.minumum);

		this.hasExtraSizeWidth = !!this.hasExtraSizeWidth;
		this.hasExtraSizeHeight = !!this.hasExtraSizeHeight;

		this.sizeValue[type] = size;

		if (type === 'width') {
			this.valueWidth = size;
			this.widthSet = true;
			this.hasExtraSizeWidth =
				+this.sizeValue.width < +this.serverSizeData.width.minumum || +this.sizeValue.width > +this.serverSizeData.width.maximum;
		}
		if (type === 'height') {
			this.valueHeight = size;
			this.heightSet = true;
			this.hasExtraSizeHeight =
				this.sizeValue.height < this.serverSizeData.height.minumum || +this.sizeValue.height > +this.serverSizeData.height.maximum;
		}

		if (this.dontSet && this.isSizeValid()) {
			this.dontSet = false;
		}

		if (this.isDesignType) {
			this.engineService.setSize({
				[type]: modelSize > max ? max : modelSize <= min ? min : modelSize,
			});
		}

		this.sliderSizeUpdate.next({ type, size });
	}

	addSliderUpdateSubscription(): void {
		this.sliderSizeUpdate
			.asObservable()
			.pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
			.subscribe(({ type, size }) => {
				this.storageSizes.size = {
					...this.storageSizes.size,
					...{
						[type]: size,
						extra_size: this.hasExtraSizeWidth || this.hasExtraSizeHeight,
						configured: !this.dontSet,
					},
				};

				if (this.blindType === 'interior') {
					this.getInteriorTopStyle();
				}

				this.dataValue[type] = this.storageSizes.size[type];

				this.sizeData.emit(this.storageSizes);

				if (this.isDesignType) {
					this.shareDataService.setBlindSizeChanged({ id: this.blindId });
				}

				this.partAvailabilityService.updateBlind(this.blindId);
				this.saveBlindSetup();
			});
	}

	onInputChangeHandler(type, event, gizmoOptions?, physicalInput = false): void {
		const modelSize = this.sizeToPointConverter(event);
		const max = this.sizeToPointConverter(this.serverSizeData[type]?.maximum);
		const min = this.sizeToPointConverter(this.serverSizeData[type]?.minumum);
		const notStartFromZeroRegExp: RegExp = /^[1-9][0-9]*$/;
		const hasExtraSize = () =>
			!notStartFromZeroRegExp.test(event) || +event < +this.serverSizeData[type].minumum || +event > +this.serverSizeData[type].maximum;

		this.hasExtraSizeWidth = !!this.hasExtraSizeWidth;
		this.hasExtraSizeHeight = !!this.hasExtraSizeHeight;

		if (type === 'width') {
			if (physicalInput) {
				this.widthSet = Boolean(+event);
			}
			this.hasExtraSizeWidth = hasExtraSize();
		}

		if (type === 'height') {
			if (physicalInput) {
				this.heightSet = Boolean(+event);
			}
			this.hasExtraSizeHeight = hasExtraSize();
		}

		if ((this.dontSet && this.isSizeValid()) || !this.variant.isShop()) {
			this.dontSet = false;
		} else if (!this.widthSet || !this.heightSet) {
			this.dontSet = true; // Maybe should set this to this.variant.isShop() instead?
		}

		if (Math.round(event).toString().length) {
			this.setModelSizes(type, +event);

			if (+this.storageSizes.size[type] !== +event || gizmoOptions?.dragStatus === false) {
				this.storageSizes.size = {
					...this.storageSizes.size,
					...{
						[type]: event,
						extra_size: this.hasExtraSizeWidth || this.hasExtraSizeHeight,
						configured: !this.dontSet,
					},
				};

				this.dataValue[type] = this.storageSizes.size[type];

				if (this.blindType === 'interior') {
					this.getInteriorTopStyle();
				}

				this.sizeData.emit(this.storageSizes);
				this.saveBlindSetup();

				if (!gizmoOptions?.dragStatus) {
					this.saveSessionBlind.PutStorageDataToServer();
				}
			}
		}

		if (this.isDesignType) {
			this.engineService.setSize({
				[type]: modelSize > max ? max : modelSize <= min ? min : modelSize,
				isBoundingBox: gizmoOptions?.isBoundingBox,
			});
		}

		this.shareDataService.setBlindSizeChanged({ id: this.blindId });
		this.partAvailabilityService.updateBlind(this.blindId);
	}

	mouseLeave() {
		if (+this.storageSizes.size['width'] > 100 && +this.storageSizes.size['height'] > 100 && this.storageSizes.size['configured']) {
			this.commitInputChange();
		}
	}

	commitInputChange(): void {
		setTimeout(() => this.saveBlindSetup(this.storageSizes, true, true)); // Timeout to ensure all pointer events have cleared the queue before deciding if next section need to be accordianed or not
	}

	setBlindSelectId(data): void {
		this.blindType = this.sessionStorageService.getBlindItemById(data?.id, STORAGE_NAMES.zip_blind_data)[0]?.type || this.blindType;
		this.getStorageMaterialType();
		this.getServerDefaultData();
		this.getStorageSizes();

		if (this.setServerData && typeof data !== 'string' && data !== undefined && this.isDesignType) {
			if (data.status === 'new') {
				this.storageSizes.size = {
					width: this.serverSizeData.width.default,
					height: this.serverSizeData.height.default,
				};
			}

			if (data.id === this.blindId || data.status === 'new') {
				if (_.isEmpty(this.defaultSizeData)) {
					for (const key of Object.keys(this.serverSizeData)) {
						this.setModelSizes(key, this.serverSizeData[key].default);
					}
				}

				for (const [key, value] of Object.entries(this.storageSizes.size)) {
					if (key !== 'configured') {
						this.setModelSizes(key, +value);
						this.onInputChangeHandler(key, value);
						this.dataValue[key] = value;
					}
				}
			}

			if (data.id === this.blindId && this.isDesignType) {
				this.sessionStorageService.setBlindData(this.blindId, STORAGE_NAMES.zip_current_blind_id);
			}
		}

		this.changeDetection.markForCheck();
	}

	setMaterialTypeSize(type): void {
		this.currentStorageMaterialType = this.storageMaterialType || type;
		this.storageMaterialType = type;
		this.getServerDefaultData();
		this.setDefaultSizeValues();
		this.changeDetection.markForCheck();
	}

	setModelSizes(key: string, value: number, isDefault?: boolean): void {
		if (!this.serverSizeData[key]) {
			return;
		}

		let sizeInPoints =
			value > +this.serverSizeData[key].maximum
				? this.serverSizeData[key].maximum
				: value < +this.serverSizeData[key].minumum
					? this.serverSizeData[key].minumum
					: value; // NOTE: minimum is spelt incorrectly as 'minumum' throughout the code base as well as in the backend code base
		sizeInPoints = this.sizeToPointConverter(sizeInPoints);

		if (key === 'width') {
			this.modelSize = value;
			this.valueWidth = value;
		}

		if (key === 'height') {
			this.modelSizeVert = value;
			this.valueHeight = value;
		}

		if (this.isDesignType || isDefault) {
			this.engineService.setSize({ [key]: sizeInPoints });
		}

		if (this.valueHeight && this.valueWidth) {
			this.partAvailabilityService.updateBlind(this.blindId);
		}
	}

	setDefaultSizeValues(): void {
		if (this.setServerData && _.isEmpty(this.storageSizes.size) && this.serverSizeData) {
			for (const key of Object.keys(this.serverSizeData)) {
				this.setModelSizes(key, +this.serverSizeData[key].default, true);
				this.dataValue[key] = +this.serverSizeData[key].default;
			}

			this.setDefaultSizeToStorage();
		}

		if (this.currentStorageMaterialType !== this.storageMaterialType && this.serverSizeData) {
			this.hasExtraSizeWidth =
				this.storageSizes.size['width'] < +this.serverSizeData.width.minumum ||
				this.storageSizes.size['width'] > +this.serverSizeData.width.maximum;
			this.hasExtraSizeHeight =
				this.storageSizes.size['height'] < +this.serverSizeData.height.minumum ||
				this.storageSizes.size['height'] > +this.serverSizeData.height.maximum;

			if (this.sizeValue.width) {
				this.valueWidth = this.sizeValue.width;
			}

			if (this.sizeValue.height) {
				this.valueHeight = this.sizeValue.height;
			}

			this.storageSizes.size = {
				...this.storageSizes.size,
				extra_size: this.hasExtraSizeHeight || this.hasExtraSizeWidth,
			};

			this.saveBlindSetup();
			this.partAvailabilityService.updateBlind(this.blindId);
			this.saveSessionBlind.PutStorageDataToServer();
		}
	}

	setIVBlindSetup(): void {
		const currentBlindId = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_current_blind_id);
		if (this.isDesignType || (currentBlindId !== this.blindId && !this.isDesignType)) {
			return;
		}

		for (const key of Object.keys(this.storageSizes.size)) {
			this.setModelSizes(key, this.storageSizes.size[key]);
		}

		if (this.blindType === 'interior') {
			setTimeout(this.getInteriorTopStyle.bind(this, false));
		}
	}

	setDefaultSizeToStorage(): void {
		this.storageSizes.size = { ...this.dataValue, configured: !this.dontSet };
		this.sizeData.emit(this.storageSizes);
		this.saveBlindSetup(this.storageSizes, false);
		this.saveSessionBlind.PutStorageDataToServer();
	}

	getServerDefaultData(): void {
		this.blindType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
		this.setCorrectSizesHandler(this.sessionConfig[this.blindType].material);
		this.setServerData = true;

		const sizes = this.sessionConfig[this.blindType].material.find((material) => material.is_default)?.sizes;

		this.engineService.setDefaultModelSizes({
			height: this.sizeToPointConverter(sizes['height'].default),
			width: this.sizeToPointConverter(sizes['width'].default),
		});
	}

	openSizeItem(): void {
		const title = $('app-size-change').parent(SELECTORS.config_item_content).prev(SELECTORS.config_item_title);

		if (!title.parent().hasClass('active') && !this.firstOpenSizeError) {
			title.click();
			this.firstOpenSizeError = true;
		}
	}

	inputValidate(event: KeyboardEvent) {
		const numberRegex: RegExp = /^\d+$/;

		if (!numberRegex.test(event.key)) {
			event.preventDefault();
		}
	}

	getInteriorTopStyle(saveChangeTime = true): void {
		const setupData = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0].setup;

		if (!setupData) {
			return;
		}

		const width = +this.storageSizes.size['width'];
		const height = +this.storageSizes.size['height'];
		const topStyleId = INTERIOR_FRAME_TOP_STYLE.reduce((acc, x) => (!acc && width > x.width && height > x.height ? x.id : acc), '');

		setupData.frames.top_style = this.sessionConfig[this.blindType].frame.top_style.filter((el) => el.id === topStyleId)[0];

		this.saveBlindSetup(setupData, saveChangeTime);
	}

	saveBlindSetup(data = this.storageSizes, saveChangeTime = true, openNextSection = false): void {
		this.isDesignType = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.design;

		this.sessionStorageService.setBlindSetupDataById(this.blindId, data, STORAGE_NAMES.zip_blind_data, saveChangeTime);
		this.updateConfiguredMark(openNextSection);
	}

	sizeToPointConverter(size: string | number): number {
		return +size / 1000;
	}

	viewTypeHandler(): void {
		this.getViewType = this.shareDataService.getViewType.subscribe((type) => (this.isDesignType = type === VIEW_TYPES.design));
	}

	setAvailabilitySubs(): void {
		this.availabilitySubs = this.shareDataService.getPartAvailability
			.pipe(filter(({ blindId }) => blindId === this.blindId))
			.subscribe(this.onAvailabilityChanges.bind(this));
	}

	onAvailabilityChanges(statuses: BlindPartsAvailability): void {
		if (statuses.availability) {
			const availabilityDependency = [SectionKeysEnum.Size, SectionKeysEnum.Material, SectionKeysEnum.Operation];
			const blindSetup: BlindSetup = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)?.[0]?.setup;

			if (!blindSetup) {
				return;
			}

			const messages = [];
			Object.entries(statuses.availability).forEach(([key, availabilities]) => {
				availabilities.forEach((availability) => {
					if (
						!availability.valid &&
						availabilityDependency.includes(key as SectionKeysEnum) &&
						(blindSetup[key].id === availability.partId || (!blindSetup[key].id && !availability.partId))
					) {
						messages.push(availability.message);
					}
				});
			});

			if (messages.length) {
				this.accordionSizeSection();
			}

			this.isSizeBlockedByAvailability = statuses.availability?.size.some((part) => part.status === 'block');
			this.availabilityWarningMessages = [...new Set(messages)];
			this.updateConfiguredMark(!this.isSizeBlockedByAvailability);
			this.changeDetection.markForCheck();
		}
	}

	updateConfiguredMark(openNextSection: boolean): void {
		if (!this.dontSet && !this.isSizeBlockedByAvailability) {
			this.blindStateService.markConfigured(this.blindId, SectionKeysEnum.Size, openNextSection && !this.isBlindWasConfigured());
		} else {
			this.blindStateService.markUnconfigured(this.blindId, SectionKeysEnum.Size);
		}
	}

	isBlindWasConfigured(): boolean {
		const missingSections = this.blindStateService.missingSections(this.blindId);
		return missingSections.length === 1 && missingSections.includes(SectionKeysEnum.Size);
	}

	accordionSizeSection(): void {
		const isSizeSectionConfigured = !this.blindStateService.missingSections(this.blindId).includes(SectionKeysEnum.Size);
		if (isSizeSectionConfigured) {
			this.shareDataService.setOpenSection({ blindId: this.blindId, section: SectionKeysEnum.Size, openToolBar: true });
		}
	}

	isSizeValid(): boolean {
		return !this.hasExtraSizeHeight && !this.hasExtraSizeWidth && this.widthSet && this.heightSet;
	}

	protected openMeasureModal(): void {
		this.modalService.openResponsiveDialog({
			component: DynamicCarouselModalComponent,
			...this.modalService.getConfig(DynamicCarouselModalComponent.ID),
			data: MEASURE_GUIDE_MOCK,
		});
	}
}
